// OLMPS home — ONE spatial canvas, no lens tabs.
//
//        [ COMPASS ]      Mountain world, compass pinned on the peak
//             ▼▲
//        [ ORB = NOW ] ▶ [ TOOL ]   opens here; the orb hangs from the compass
//             ▼▲
//         [ SELF ]
//
// Every screen is a full screen; each move is a full-screen pan (arrows or
// swipes) of one big board, and the backdrop descends from mountain sky into
// deep-navy starfield so it reads as one world. The orb IS the NOW screen.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, PanResponder, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Menu, Feather } from 'lucide-react-native';
import type { GearId } from '../olmps/ascension';
import type { UseTodayData } from '../olmps/useTodayData';
import type { Gear } from '../domain/olmps';
import { ORB_ROW_TOOL, type OrbCtx } from '../olmps/CompassView';
import { EdgeTabs, NAV_D } from './EdgeTabs';
import { ToolScreen } from './ToolScreen';
import { readNav, readNavSync, writeNav } from '../lib/navPersist';
import { supabase } from '../lib/supabase';
import { useCurrentStep, type NowStep } from './useCurrentStep';
import { useSelf } from './useSelf';
import { effectiveAims } from '../olmps/oracle-spine-data';
import { NowOrb, timeGear } from './NowView';
import { LifeView, PEAK_FRAC, type LifeMode } from './LifeView';
import { SelfView } from './SelfView';
import { DeitySheet } from './DeitySheet';
import { TEMPLE_HALL, SEAT_BY_ID } from './roster';
import { DISPLAY_FONT, makeStyles, useV10 } from './theme';
import { GlassFill } from './ui';
import { OrbitBackdrop, NightBackdrop, DayBackdrop, useOrbit } from './orbit';
import { FlowRibbon, straight } from './FlowLine';

export type CanvasPos = 'compass' | 'orb' | 'self' | 'tool';
/** Grid cell (col,row) of each screen on the board. */
const GRID: Record<CanvasPos, [number, number]> = { compass: [0, 0], orb: [0, 1], self: [0, 2], tool: [1, 1] };
const posAt = (c: number, r: number) => (Object.keys(GRID) as CanvasPos[]).find((k) => GRID[k][0] === c && GRID[k][1] === r) ?? null;
type SavedCanvas = { pos: CanvasPos; tool: string | null };
const PAN_MS = 520;

export interface V10HomeProps {
  userId: string | undefined;
  today: UseTodayData;
  onOpenTool: (toolId: string, gear?: GearId, opts?: { initialTaskId?: string }) => void;
  onOpenDrawer: () => void;
  onCapture: () => void;
  onOpenAstra: () => void;
  /** Open the morning / evening session (the orb's O and final R rows). */
  onOpenSession?: (kind: 'morning' | 'night') => void;
}

export function V10Home({ userId, today, onOpenTool, onOpenDrawer, onCapture, onOpenAstra, onOpenSession }: V10HomeProps) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();

  const saved = useRef(readNavSync<SavedCanvas>('v10canvas')).current;
  const [pos, setPosState] = useState<CanvasPos>(saved?.pos === 'self' || saved?.pos === 'compass' || (saved?.pos === 'tool' && saved.tool) ? saved.pos : 'orb');
  const [lifeMode, setLifeModeState] = useState<LifeMode>(() => readNavSync<LifeMode>('v10life') ?? 'compass');
  const setLifeMode = (m: LifeMode) => {
    setLifeModeState(m);
    writeNav('v10life', m);
  };
  const [stepIdx, setStepIdx] = useState(0);
  const [deityId, setDeityId] = useState<string | null>(null);
  const [orbCtx, setOrbCtx] = useState<OrbCtx>({ spokeLabel: '', sector: null, stage: 'T', stageLabel: 'Today', rungState: '' });

  // Orb row state: which row is open, which row's tool the ▶ screen shows.
  const now = today.now ?? new Date();
  const dayGear = timeGear(now);
  const [openOverride, setOpenOverride] = useState<GearId | null>(null);
  const [selOverride, setSelOverride] = useState<GearId | null>(null);
  const openGear = openOverride ?? dayGear;
  const selectedGear = selOverride ?? openGear;
  const [stepTool, setStepTool] = useState<{ id: string; opts?: { initialTaskId?: string } } | null>(
    saved?.tool && saved.pos === 'tool' ? { id: saved.tool } : null,
  );
  const toolId = stepTool?.id ?? ORB_ROW_TOOL[selectedGear];
  const [toolMounted, setToolMounted] = useState(pos === 'tool');

  const { steps, pace, loading, projects, spine, refresh } = useCurrentStep(userId, today);
  const self = useSelf(userId);
  const step: NowStep | null = steps[stepIdx] ?? steps[0] ?? null;
  const orbit = useOrbit(step?.sector ?? null);
  useEffect(() => {
    if (stepIdx >= steps.length && steps.length) setStepIdx(0);
  }, [steps.length, stepIdx]);

  // "Why off / how back" — one line, kept for the day.
  const dayKey = now.toDateString();
  const [whySaved, setWhySaved] = useState(() => readNavSync<{ d: string; t: string }>('orbwhy'));
  const why = whySaved && whySaved.d === dayKey ? whySaved.t : '';
  const setWhy = (text: string) => {
    const v = { d: dayKey, t: text };
    setWhySaved(v);
    writeNav('orbwhy', v);
  };

  // ---- layout (all in canvas-panel coordinates) -----------------------------
  const headerH = insets.top + 52;
  const pillTop = headerH + 2;
  // Mountain frame: at least screen-wide and tall enough to fill the lower
  // screen; the peak (PEAK_FRAC down the frame) sits where the compass is.
  const frameSize = Math.round(Math.max(winW, winH * 0.62));
  const anchorY = Math.round(Math.max(headerH + 150, winH * 0.32));
  const frameTop = Math.round(anchorY - frameSize * PEAK_FRAC);
  const frameLeft = Math.round((winW - frameSize) / 2);
  const bottomY = winH;
  // The orb: large, centred, nearly the full width of the screen.
  const orbSize = Math.round(Math.max(220, Math.min(winW * 0.92, 460, winH - headerH - insets.bottom - 64)));
  // The orb sits at the centre of the page area, so its ribbons run straight
  // out to the four direction circles.
  const orbCy = Math.round((headerH + winH - insets.bottom) / 2);
  const orbTop = orbCy - Math.round(orbSize / 2);
  const navC = 6 + NAV_D / 2;

  // ---- the canvas -----------------------------------------------------------
  const posRef = useRef(pos);
  const dims = useRef({ w: winW, h: winH, headerH });
  dims.current = { w: winW, h: winH, headerH };
  const modeRef = useRef(lifeMode);
  modeRef.current = lifeMode;
  const toolIdRef = useRef(toolId);
  toolIdRef.current = toolId;
  const tx = useRef(new Animated.Value(-GRID[pos][0] * winW)).current;
  const ty = useRef(new Animated.Value(-GRID[pos][1] * winH)).current;
  // Keep the resting offset right if the window is resized.
  useEffect(() => {
    tx.setValue(-GRID[posRef.current][0] * winW);
    ty.setValue(-GRID[posRef.current][1] * winH);
  }, [winW, winH, tx, ty]);

  const goTo = (p: CanvasPos) => {
    posRef.current = p;
    setPosState(p);
    if (p === 'tool') setToolMounted(true);
    writeNav('v10canvas', { pos: p, tool: p === 'tool' ? toolIdRef.current : null } satisfies SavedCanvas);
    const cfg = { duration: PAN_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: true };
    Animated.parallel([
      Animated.timing(tx, { toValue: -GRID[p][0] * dims.current.w, ...cfg }),
      Animated.timing(ty, { toValue: -GRID[p][1] * dims.current.h, ...cfg }),
    ]).start(({ finished }) => {
      if (finished && posRef.current !== 'tool') setToolMounted(false);
    });
  };
  const goRef = useRef(goTo);
  goRef.current = goTo;

  // Native restores the saved position once storage has loaded.
  useEffect(() => {
    if (saved) return;
    void readNav<SavedCanvas>('v10canvas').then((s) => {
      if (!s || s.pos === 'orb') return;
      if (s.pos === 'tool' && s.tool) setStepTool({ id: s.tool });
      if (s.pos === 'self' || s.pos === 'compass' || (s.pos === 'tool' && s.tool)) goRef.current(s.pos);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Swipes: ▲ (up) from the orb → SELF, ◀ (left) → tool; the reverse brings
  // you home. SELF/tool only give up their own scrolling at their edge/top.
  const axis = useRef<'x' | 'y' | null>(null);
  /** Neighbour screen reached by dragging by (dx,dy) from `p`, if any. */
  const neighbour = (p: CanvasPos, a: 'x' | 'y', d: number): CanvasPos | null => {
    const [c, r] = GRID[p];
    // Dragging up/left reveals the screen below/right.
    return a === 'y' ? posAt(c, r + (d < 0 ? 1 : -1)) : posAt(c + (d < 0 ? 1 : -1), r);
  };
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => {
        const p = posRef.current;
        const ay = Math.abs(g.dy);
        const ax = Math.abs(g.dx);
        const a: 'x' | 'y' | null = ay > 22 && ay > ax * 1.5 ? 'y' : ax > 22 && ax > ay * 1.5 ? 'x' : null;
        if (!a) return false;
        const d = a === 'y' ? g.dy : g.dx;
        if (!neighbour(p, a, d)) return false;
        // Mountain mode: horizontal drags turn the mountain.
        if (p === 'compass' && a === 'x') return false;
        // SELF scrolls: only the top strip hands over a downward pull. Tools: left edge only.
        if (p === 'self' && !(g.y0 < dims.current.headerH + 140)) return false;
        if (p === 'tool' && !(g.x0 < 40)) return false;
        axis.current = a;
        return true;
      },
      onPanResponderMove: (_e, g) => {
        const { w, h } = dims.current;
        const p = posRef.current;
        const a = axis.current;
        if (!a) return;
        const d = a === 'y' ? g.dy : g.dx;
        const dest = neighbour(p, a, d);
        const lim = a === 'y' ? h : w;
        const dd = dest ? Math.max(-lim, Math.min(lim, d)) : 0;
        if (a === 'y') ty.setValue(-GRID[p][1] * h + dd);
        else tx.setValue(-GRID[p][0] * w + dd);
      },
      onPanResponderRelease: (_e, g) => {
        const p = posRef.current;
        const a = axis.current;
        axis.current = null;
        if (!a) return goRef.current(p);
        const d = a === 'y' ? g.dy : g.dx;
        const v = a === 'y' ? g.vy : g.vx;
        const dest = neighbour(p, a, d);
        goRef.current(dest && (Math.abs(d) > 80 || Math.abs(v) > 0.6) ? dest : p);
      },
      onPanResponderTerminate: () => {
        axis.current = null;
        goRef.current(posRef.current);
      },
    }),
  ).current;

  // ---- actions --------------------------------------------------------------
  const pickRow = (g: GearId) => {
    setOpenOverride(g);
    setSelOverride(g);
    setStepTool(null);
    toolIdRef.current = ORB_ROW_TOOL[g];
  };
  const openStepTool = (s: NowStep) => {
    if (s.designId) writeNav('tkne', { designId: s.designId });
    setStepTool({ id: s.tool, opts: s.toolOpts });
    toolIdRef.current = s.tool;
    goTo('tool');
  };
  const goRight = () => {
    toolIdRef.current = toolId;
    goTo('tool');
  };
  const markDone = (s: NowStep) => {
    const id = s.toolOpts?.initialTaskId;
    if (s.source !== 'kyrs' || !id || !userId) return;
    supabase
      .from('tasks')
      .update({ done: true })
      .eq('id', id)
      .eq('user_id', userId)
      .then(() => refresh());
  };
  const openSeatTool = (seatId: string) => {
    const seat = SEAT_BY_ID[seatId];
    setDeityId(null);
    if (!seat) return;
    if (step && step.tool === seat.shellTool) return openStepTool(step);
    if (seat.shellTool === 'astrs') return onOpenAstra();
    onOpenTool(seat.shellTool, seat.gear);
  };

  const orbNode = (
  
    <NowOrb
      size={orbSize}
      spokeLabel={orbCtx.spokeLabel}
      sector={orbCtx.sector}
      stageLabel={orbCtx.stageLabel}
      rungState={orbCtx.rungState}
      steps={steps}
      stepIdx={Math.min(stepIdx, Math.max(0, steps.length - 1))}
      onStepIdx={setStepIdx}
      pace={pace}
      self={self}
      loading={loading}
      now={now}
      openGear={openGear}
      selectedGear={selectedGear}
      onPickRow={pickRow}
      why={why}
      onWhy={setWhy}
      onTalk={(id) => setDeityId(id)}
      onOpenStepTool={openStepTool}
      onDone={step?.source === 'kyrs' ? markDone : undefined}
      onOpenTool={(id) => {
        setStepTool({ id });
        toolIdRef.current = id;
        goTo('tool');
      }}
      onOpenSession={onOpenSession}
      onSetDirection={() => onOpenTool('oracle', 'orient')}
    />
  );

  return (
    <View style={styles.root}>
      {/* ONE constant background, outside the panning canvas: it never moves or changes between screens. */}
      <NightBackdrop width={winW} height={winH} />
      <Animated.View style={{ width: winW * 2, height: winH * 3, transform: [{ translateX: tx }, { translateY: ty }] }} {...pan.panHandlers}>
        {/* COMPASS screen (0,0): the Mountain world + the pinned compass. */}
        <View style={[styles.panel, { left: 0, top: 0, width: winW, height: winH }]}>
          <OrbitBackdrop frame={orbit.frame} width={winW} top={frameTop} size={frameSize} />
          <LifeView
            userId={userId}
            current={step}
            projects={projects}
            spine={spine}
            orbit={orbit}
            frameTop={frameTop}
            frameSize={frameSize}
            pillTop={pillTop}
            bottomY={bottomY}
            mode={lifeMode}
            onMode={setLifeMode}
            onOpenTool={(tool) => onOpenTool(tool)}
            onOrbCtx={setOrbCtx}
            onNodePick={() => goTo('orb')}
            nodeText={(sector, stage) => {
              const node = sector && spine ? spine[sector] : null;
              if (stage === 'E') return node?.endeavour || null;
              if (stage === 'G') return node?.goal || null;
              if (stage === 'A') return (node ? effectiveAims(node)[0]?.label : null) || null;
              const list = sector ? projects[sector] ?? [] : [];
              if (stage === 'T') return (step && step.sector === sector ? step.title : null) || list[0]?.name || null;
              return (step && step.sector === sector ? step.title : null) || list[0]?.steps.find((x) => x.status !== 'C')?.text || null;
            }}
            frameLeft={frameLeft}
            anchorY={anchorY}
            bottomInset={insets.bottom}
            defaultGear={dayGear}
          />
          <EdgeTabs top={headerH} bottom={insets.bottom} down={() => goTo('orb')} />
        </View>

        {/* ORB screen (0,1) = NOW: the orb, with the connector entering from the top. */}
        <View style={[styles.panel, { left: 0, top: winH, width: winW, height: winH }]}>
          {/* Four currents out of the rim (behind the arrows) to the screen edges; they meet the neighbouring screens' ribbons/tabs exactly. */}
          <FlowRibbon
            width={winW}
            height={winH}
            active={pos === 'orb' || pos === 'compass' || pos === 'self'}
            lines={[
              straight({ x: winW / 2, y: orbTop + 4 }, { x: winW / 2, y: headerH + navC }),
              straight({ x: winW / 2, y: orbTop + orbSize - 4 }, { x: winW / 2, y: winH - insets.bottom - navC }),
              straight({ x: (winW - orbSize) / 2 + 4, y: orbCy }, { x: navC, y: orbCy }),
              straight({ x: (winW + orbSize) / 2 - 4, y: orbCy }, { x: winW - navC, y: orbCy }),
            ]}
          />
          <View
            style={[
              styles.orb,
              { top: orbTop, left: (winW - orbSize) / 2, width: orbSize, height: orbSize, borderRadius: orbSize / 2 },
            ]}
          >
            <View pointerEvents="none" style={[StyleSheet.absoluteFill, { borderRadius: orbSize / 2, overflow: 'hidden' }]}>
              <GlassFill radius={orbSize / 2} tone="strong" />
            </View>
            {orbNode}
          </View>
          <EdgeTabs top={headerH} bottom={insets.bottom} up={() => goTo('compass')} down={() => goTo('self')} right={goRight} />
        </View>

        {/* SELF panel (0,1): the temple hall, full screen height. */}
        <View style={[styles.panel, { left: 0, top: winH * 2, width: winW, height: winH }]}>
          <View style={{ flex: 1, paddingTop: headerH, paddingBottom: insets.bottom }}>
            <SelfView self={self} step={step} onOpenDeity={(id) => setDeityId(id)} onBack={() => goTo('orb')} />
          </View>
        </View>

        {/* TOOL panel (1,0): the tool selected in the orb. */}
        <View style={[styles.panel, { left: winW, top: winH, width: winW, height: winH }]}>
          {toolMounted ? (
            <ToolScreen
              toolId={toolId}
              toolOpts={stepTool?.opts}
              fallbackGear={(stepTool ? step?.gear ?? selectedGear : selectedGear) as Gear}
              width={winW}
              height={winH}
              top={headerH}
              bottom={insets.bottom}
              onBack={() => goTo('orb')}
              onSelectTool={(id) => {
                setStepTool({ id });
                toolIdRef.current = id;
                writeNav('v10canvas', { pos: 'tool', tool: id } satisfies SavedCanvas);
              }}
              onOpenTool={(id, opts) => {
                setStepTool({ id, opts });
                toolIdRef.current = id;
              }}
            />
          ) : null}
        </View>
      </Animated.View>

      {/* Global chrome stays put while the canvas moves under it. */}
      <View pointerEvents="box-none" style={[styles.top, { paddingTop: insets.top + 6 }]}>
        <Pressable onPress={onOpenDrawer} style={styles.iconBtn} hitSlop={8}>
          <Menu size={20} color={t.ivory} />
        </Pressable>
        <Text style={styles.brand}>OLMPS</Text>
        <Pressable onPress={onCapture} style={styles.iconBtn} hitSlop={8} accessibilityLabel="Capture">
          <Feather size={18} color={t.goldBright} />
        </Pressable>
      </View>

      <DeitySheet
        seatId={deityId}
        seatView={deityId ? self.all.find((v) => v.seat.id === SEAT_BY_ID[deityId]?.id) : undefined}
        step={step}
        onClose={() => setDeityId(null)}
        onOpenTool={openSeatTool}
      />
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: t.night, overflow: 'hidden' },
    orb: { position: 'absolute', borderWidth: 2, borderColor: t.gold, overflow: 'visible', boxShadow: `0 0 34px ${t.goldGlow}` } as object,
    panel: { position: 'absolute', overflow: 'hidden' },
    top: { position: 'absolute', left: 0, right: 0, top: 0, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, zIndex: 30 },
    iconBtn: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
    brand: { fontFamily: DISPLAY_FONT, color: t.goldBright, fontSize: 24, letterSpacing: 6 },
  }),
);
